/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './SidebarHeader';

const SidebarHeaderManager: ComponentManagerFn<'SidebarHeader'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarHeader'>(platform, 'SidebarHeader'),
    type: 'SidebarHeader',
    name: 'Sidebar Header',
    isChildAllowed: (page, item, childType) =>
      (['IconButton'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['IconButton'],
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    getDisplayText: (item) => item.props.title.value,
    canHaveChildren: () => true,
    showEmptyPlaceholderInDesignerView: false,
    category: 'Sidebar',
    icon: { iconName: 'flute' },
    description: `**Overview**<br/>
The Sidebar Header is a structural component used to define the top section of a Sidebar Panel. It typically contains a title and optional action buttons (like a "Close" or "Menu" icon), providing context and controls for the content within the sidebar.
Use this component to establish a consistent visual hierarchy at the top of your custom sidebars.

**1. Configuration**<br/>
* **Title:** The primary text displayed in the header. This property supports expressions, allowing you to dynamically change the header text based on application state (e.g., displaying the name of the currently selected record: "Details: " + $._.name).

**2. Structure & Children**<br/>
The Sidebar Header acts as a container for specific controls, usually aligned to the right of the title.
* **Default Child:** It automatically includes an IconButton when added, which is commonly configured as a "Close" (X) button or an "Options" (Kebab) menu.
* **IconButton:** You can add one or more icon buttons to provide actions directly within the header area.

**3. Best Use Cases**<br/>
* **Contextual Panels:** Displaying "Edit Customer" as the title at the top of a slide-out edit form.
* **Navigation Drawers:** Placing the app logo or "Main Menu" text at the top of a navigation sidebar.
* **Toolboxes:** Labeling a palette of drag-and-drop items (e.g., "Components Library") with a "Minimize" button in the corner.`,
  };
};

export default SidebarHeaderManager;
